import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import * as actions from "../../../ducks/app/file-uploads";
import { useAppDispatch } from "../../../redux-store";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isMobile } from "../../../helpers/mobile";
import {
  DocumentScanningFlow,
  PdfConfiguration,
  ScanbotBinarizationFilter,
  ScanbotDocument,
  ScanbotPdfGenerator,
  ScanbotSDK,
} from "capacitor-plugin-scanbot-sdk";
import * as Sentry from "@sentry/capacitor";
import LoadingButton from "../../../components/LoadingButton";
import { useParams } from "react-router";
import useShowUploadError from "./useShowUploadError";

const AddScan = ({
  cargoId,
  stopId,
  // scanCount,
  onSuccess,
}: {
  cargoId: string;
  stopId: string;
  // scanCount: number;
  onSuccess?: () => void;
}) => {
  const { t, i18n } = useTranslation(["stopPage", "translation"]);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const showUploadError = useShowUploadError();
  const params = useParams();
  const truckId = (params.truckId as string) || "";

  const uploadScanSignedLink = async (image: {
    filePath: string;
    name: string;
  }) => {
    setLoading(true);
    try {
      const s3Identifier = await dispatch(
        actions.uploadFileToS3Mobile({
          contentType: "application/pdf",
          path: image.filePath,
        })
      ).unwrap();

      await dispatch(
        actions.addFileReference({
          cargoId,
          stopId,
          truckId,
          file: {
            name: image.name,
            mimeType: "application/pdf",
            documentType: "POD",
            s3Identifier,
          },
        })
      ).unwrap();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      showUploadError(err, { reported: true });
    } finally {
      setLoading(false);
    }
  };

  const takeScan = async () => {
    try {
      /** Always make sure you have a valid license on runtime via ScanbotSDK.getLicenseInfo() */
      const scanbotLicenseInfo = await ScanbotSDK.getLicenseInfo();
      if (!scanbotLicenseInfo?.isValid) {
        alert("Invalid setup");
        Sentry.captureMessage("scanbot license info", {
          extra: { licenseInfo: scanbotLicenseInfo },
        });
        Sentry.captureException(new Error("Invalid scanbot license setup"));
        return;
      }

      const i18nBundle = i18n.getResourceBundle(i18n.language, "stopPage");
      /**
       * Create the document scanning flow configuration, customize it per your needs and
       * start the document scanner with the configuration
       */
      const configuration = new DocumentScanningFlow({
        appearance: { navigationBarMode: "DARK" },
        palette: { sbColorPrimary: "#212121" },
        outputSettings: {
          pagesScanLimit: 0, // Unlimited
          defaultFilter: new ScanbotBinarizationFilter({
            outputMode: "ANTIALIASED",
          }),
        },
        localization: i18nBundle.scanbot,
      });

      /** Start the Document Scanner */
      const documentResult = await ScanbotDocument.startScanner(configuration);

      /**
       * Handle the result if status is OK
       */
      if (documentResult.status === "OK") {
        /** Upload as PDF */
        const documentImageURIs = documentResult.data.pages
          .map((page) => page.documentImageURI)
          .filter(Boolean) as string[];
        const pdfFileUri = await ScanbotPdfGenerator.generateFromImages({
          images: documentImageURIs,
          pdfConfiguration: new PdfConfiguration({
            attributes: {
              author: "",
              creator: "",
              title: "",
              subject: "",
              keywords: "",
            },
            pageSize: "A4",
            pageFit: "FIT_IN",
            pageDirection: "PORTRAIT",
            dpi: 300,
            jpegQuality: 100,
            resamplingMethod: "NONE",
          }),
        });
        await uploadScanSignedLink({
          filePath: pdfFileUri,
          name: `mobile-scan-${new Date().toISOString()}.pdf`,
        });
      }
    } catch (error: any) {
      // Cancelling resolves with status "CANCELED", so this is a real failure
      // of the scanner or the PDF generation (upload failures are handled in
      // uploadScanSignedLink).
      console.error(
        "An error has occurred while running Document Scanner",
        error.message
      );
      showUploadError(error, { reported: false });
    }
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <label>
          <LoadingButton
            loading={loading}
            variant="outlined"
            fullWidth
            size="large"
            component="span"
            startIcon={<DocumentScannerIcon />}
            onClick={() => {
              if (isMobile) {
                takeScan();
              }
            }}
          >
            <span>{t("takeScan")}</span>
            {/*<span>{scanCount ? `(${scanCount})` : ""}</span>*/}
          </LoadingButton>
        </label>
      </form>
    </>
  );
};

export default AddScan;
